<template>
  <div class="row" :class="{ selected }" @click="$emit('click')">
    <div class="icon-wrap" :class="colorClass">
      <img :src="getIconPath(type)" :alt="displayType" class="icon" />
    </div>

    <div class="info">
      <div class="top">
        <strong class="time">{{ formattedTime }}</strong>
        <span class="type" :class="typeClass">{{ displayType }}</span>
      </div>
      <div class="bottom">
        <div class="dots">
          <span
            v-for="n in capacityDots"
            :key="n"
            class="dot"
            :class="{ on: n <= booked }"
          ></span>
        </div>
        <span class="count">{{ booked }} / {{ capacity }}</span>
      </div>
    </div>

    <div class="right">
      <span v-if="finalStatus === 'done'" class="chip chip--done"
        >예약 완료</span
      >
      <span v-else-if="finalStatus === 'close'" class="chip chip--close"
        >마감</span
      >
      <span v-else-if="finalStatus === 'warn'" class="chip chip--warn"
        >마감 임박</span
      >
      <UiButton
        v-if="finalStatus === 'open' || finalStatus === 'warn'"
        class="book-btn"
        @click="$emit('book')"
        >인원추가하기</UiButton
      >
      <UiButton
        v-if="finalStatus === 'open' || finalStatus === 'warn'"
        class="trial-btn"
        @click="$emit('free-trial')"
        >체험·드랍인</UiButton
      >
    </div>
  </div>
</template>

<script>
import UiButton from "@/components/UiButton.vue";
import {
  classDisplayName,
  classTypeIcon,
  classTypeTone,
} from "@/lib/classTypes";

export default {
  name: "ScheduleRow",
  components: { UiButton },
  props: {
    time: { type: String, required: true },
    type: { type: String, required: true },
    booked: { type: Number, required: true },
    capacity: { type: Number, required: true },
    status: { type: String, default: "" },
    mine: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    // R9 타입 표시명(서버 type_label). 비우면 lib/classTypes.js 표로 정한다(예전 타입은 값 그대로)
    typeLabel: { type: String, default: "" },
    // 수업 이름(classes.title). 기타(other) 수업만 이 이름을 보인다(2026-10-24 사장님 결정 B, lib/classTypes.js classDisplayName).
    // HTML title 속성과 헷갈리지 않게 prop 이름은 classTitle이다
    classTitle: { type: String, default: "" },
  },
  computed: {
    // 표시 이름: 기타 수업은 이름(classTitle, 비면 "기타"), 그 밖에는 타입 표시명(typeLabel → 표). 색·아이콘은 타입으로 정한다
    displayType() {
      return classDisplayName({
        type: this.type,
        typeLabel: this.typeLabel,
        title: this.classTitle,
      });
    },
    typeClass() {
      return classTypeTone(this.type);
    },
    colorClass() {
      return this.typeClass;
    },
    capacityDots() {
      return Math.min(this.capacity || 12, 12);
    },
    computedStatus() {
      if (this.booked >= this.capacity) return "close";
      const remain = this.capacity - this.booked;
      if (remain <= 3) return "warn";
      return "open";
    },
    finalStatus() {
      if (this.status) return this.status;
      if (this.mine) return "done";
      return this.computedStatus;
    },
    formattedTime() {
      // "06:00:00" -> "06:00" 형식으로 변환
      if (this.time && this.time.includes(":")) {
        const parts = this.time.split(":");
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
      }
      return this.time;
    },
  },
  methods: {
    getIconPath(type) {
      try {
        return require(`@/assets/icons/${classTypeIcon(type)}.png`);
      } catch (e) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.row {
  position: relative;
  display: grid;
  grid-template-columns: 56px 1fr auto;
  align-items: center;
  gap: 16px;
  background: #0a0a0a;
  border-radius: 10px;
  padding: 18px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.row:hover {
  background: #1a1a1a;
}

.row.selected {
  background: #1a1a1a;
  border: 2px solid #78ff47;
}

.icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 5px;
  border: 1px solid;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-wrap.hyb {
  border-color: #1f472d;
  background: rgba(31, 71, 45, 0.2);
}
.icon-wrap.core {
  border-color: #4e3511;
  background: rgba(78, 53, 17, 0.2);
}
.icon-wrap.car {
  border-color: #4d1c1b;
  background: rgba(77, 28, 27, 0.3);
}

.icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.info {
  display: grid;
  gap: 8px;
  min-width: 0; /* 긴 타입 이름(Lower body & Cardio)이어도 좁은 화면(390)에서 줄이 화면 밖으로 넓어지지 않게 */
}

.top {
  display: flex;
  flex-wrap: wrap; /* 좁은 화면에서는 타입 이름이 다음 줄로 내려간다 */
  align-items: center;
  gap: 4px 12px;
}

.time {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.type {
  font-size: 18px;
  letter-spacing: -0.02em;
}

.type.hyb {
  color: #49de80;
}
.type.core {
  color: #f59e0c;
}
.type.car {
  color: #ef4444;
}

.bottom {
  display: flex;
  flex-wrap: wrap; /* 좁은 화면에서는 인원 수가 점 아래로 내려간다(숫자가 세로로 쪼개지지 않게) */
  align-items: center;
  gap: 4px 8px;
}

.dots {
  display: flex;
  gap: 4px;
}

.dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #333;
}

.dot.on {
  background: #c8c8c8;
}

.count {
  color: #8e8e8e;
  font-size: 14px;
  white-space: nowrap;
}

.right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chip {
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 14px;
}

.chip--done {
  border: 1px solid #78ff47;
  color: #78ff47;
  background: #1b1d1e;
}

.chip--close {
  border: 1px solid #333;
  color: #8e8e8e;
  background: #1b1d1e;
}

.chip--warn {
  border: 1px solid #ff817f;
  color: #ff817f;
  background: rgba(77, 28, 27, 0.3);
}

.book-btn {
  width: 79px;
  height: 28px;
  border-radius: 5px;
  font-size: 14px;
  line-height: 1;
}
.trial-btn {
  height: 28px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1;
  background: #2d1a1a;
  border: 1px solid #78ff47;
  color: #78ff47;
  padding: 0 10px;
  cursor: pointer;
}
</style>
